import React, { useMemo } from "react";
import { IconClock, IconWorld, IconUserCircle, IconAlertTriangle } from "@tabler/icons-react";

// ---- i18n ----
const dict = {
    en: {
        domain: "Domain",
        expires: "Expires",
        registrator: "Registrar",
        contact: "Contact",
        site: "Site",
        status: "Status",
        unknown: "Unknown",
        daysLeft: (d) => `${d} days left`,
        expired: "Expired",
        warnings: "Alerts",
        warnSoon: (d) => `Expires in ${d} days`,
        warnExpiredShort: "Unfortunately, the domain is expired",
        warnOk: "All good: no warnings",
        contactCard: "Contact group",
        history: "History",
        event: "Event",
        before: "Before",
        after: "After",
        historyDate: "Date",
        noHistory: "No history records",
    },
    ru: {
        domain: "Домен",
        expires: "Истекает",
        registrator: "Регистратор",
        contact: "Контакт",
        site: "Сайт",
        status: "Статус",
        unknown: "Неизвестно",
        daysLeft: (d) => `Осталось ${d} дн.`,
        expired: "Истек",
        warnings: "Предупреждения",
        warnSoon: (d) => `Истекает через ${d} дн.`,
        warnExpiredShort: "К сожалению, домен просрочен",
        warnOk: "Всё хорошо: предупреждений нет",
        contactCard: "Группа контактов",
        history: "История",
        event: "Событие",
        before: "До",
        after: "После",
        historyDate: "Дата",
        noHistory: "Записей нет",
    },
    ua: {
        domain: "Домен",
        expires: "Спливає",
        registrator: "Реєстратор",
        contact: "Контакт",
        site: "Сайт",
        status: "Статус",
        unknown: "Невідомо",
        daysLeft: (d) => `Залишилось ${d} дн.`,
        expired: "Строк минув",
        warnings: "Попередження",
        warnSoon: (d) => `Спливає через ${d} дн.`,
        warnExpiredShort: "На жаль, домен прострочено",
        warnOk: "Усе гаразд: попереджень немає",
        contactCard: "Група контактів",
        history: "Історія",
        event: "Подія",
        before: "Було",
        after: "Стало",
        historyDate: "Дата",
        noHistory: "Записів немає",
    },
};

const getInitialLang = () => (localStorage.getItem("lang") || "en").toLowerCase();

const Dashboard = ({ lang: langProp, singleArgs }) => {
    const lang = langProp || getInitialLang();
    const t = useMemo(() => dict[lang] || dict.en, [lang]);
    const details = singleArgs?.details || {};
    const logs = singleArgs?.logs || [];

    const formatDate = (date) => {
        if (!date) return t.unknown;
        const d = new Date(date);
        return Number.isNaN(d.getTime()) ? t.unknown : d.toLocaleString();
    };

    const calcDaysLeft = (date) => {
        if (!date) return t.unknown;
        const d = new Date(date);
        if (Number.isNaN(d.getTime())) return t.unknown;
        const diff = d.getTime() - Date.now();
        const days = Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
        return diff <= 0 ? t.expired : t.daysLeft(days);
    };

    const daysLeftNumeric = (() => {
        if (!details.date_expiration) return null;
        const d = new Date(details.date_expiration);
        if (Number.isNaN(d.getTime())) return null;
        return Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    })();

    const parseComment = (comment) => {
        if (!comment) return null;
        try {
            return typeof comment === "string" ? JSON.parse(comment) : comment;
        } catch (e) {
            return null;
        }
    };

    const parsedLogs = logs.map((log) => {
        const payload = parseComment(log.comment) || {};
        return {
            ...log,
            event: payload.event || log.event || t.unknown,
            domain: payload.domain_name || details.domain_name || t.domain,
            before: payload.before || null,
            after: payload.after || null,
        };
    });

    const renderSnapshot = (snapshot) => {
        if (!snapshot) return <div className="text-muted">{t.unknown}</div>;

        const rows = [
            { label: t.expires, value: formatDate(snapshot.date_expiration) },
            { label: t.registrator, value: snapshot.registrator },
            { label: t.status, value: snapshot.status || snapshot.status_text },
        ].filter((row) => row.value);

        return (
            <div className="small">
                {rows.map((row, idx) => (
                    <div key={`snap-${row.label}-${idx}`}>
                        <span className="text-muted">{row.label}:</span> <span className="fw-semibold">{row.value}</span>
                    </div>
                ))}
            </div>
        );
    };

    const infoBlocks = [
        {
            label: t.domain,
            value: details.domain_name || t.unknown,
            icon: <IconWorld size={28} color="#2563eb" />,
        },
        {
            label: t.registrator,
            value: details.registrator || t.unknown,
            icon: <IconWorld size={28} color="#0ea5e9" />,
        },
        {
            label: t.expires,
            value: formatDate(details.date_expiration),
            helper: calcDaysLeft(details.date_expiration),
            icon: <IconClock size={28} color="#9333ea" />,
        },
    ];

    const heroStyle = {
        background:
            "linear-gradient(120deg, #f3f4f6 0%, #e0f2fe 45%, #dbeafe 100%), radial-gradient(circle at 20% 15%, rgba(255,255,255,0.8), transparent 35%), radial-gradient(circle at 80% 25%, rgba(255,255,255,0.6), transparent 35%)",
        color: "#0f172a",
        boxShadow: "0 12px 32px rgba(59,130,246,0.16)",
        border: "1px solid rgba(59,130,246,0.12)",
    };

    return (
        <div className="d-flex flex-column gap-4">
            <div className="rounded-4 p-4 shadow-sm" style={heroStyle}>
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                    <div>
                        <div className="text-uppercase fw-semibold small text-muted">{t.domain}</div>
                        <h2 className="mb-3" style={{ textShadow: "0 3px 10px rgba(15,23,42,0.15)" }}>
                            {details.domain_name || t.unknown}
                        </h2>
                        <div
                            className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill fw-semibold"
                            style={{ background: "rgba(46, 204, 113, 0.15)", color: "#1e7e34" }}
                        >
                            <span className="rounded-circle" style={{ display: "inline-block", width: 10, height: 10, background: "#1e7e34" }}></span>
                            {t.status}: {details.status_text || details.status || t.unknown}
                        </div>
                    </div>
                    <div className="text-end">
                        <div className="text-uppercase fw-semibold small opacity-75">{t.expires}</div>
                        <div className="fs-3 fw-bold">{formatDate(details.date_expiration)}</div>
                        <div className="small opacity-75">{calcDaysLeft(details.date_expiration)}</div>
                    </div>
                </div>
            </div>

            <div className="row g-3">
                {infoBlocks.map((item, idx) => (
                    <div className="col-12 col-md-6 col-xl-4" key={`info-${idx}`}>
                        <div className="border rounded-4 p-3 h-100 shadow-sm bg-white">
                            <div className="d-flex align-items-center gap-3 mb-2">
                                <div className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center" style={{ width: 44, height: 44 }}>
                                    {item.icon}
                                </div>
                                <div>
                                    <div className="text-uppercase text-muted fw-semibold small">{item.label}</div>
                                    <div className="fw-semibold">{item.value || t.unknown}</div>
                                    {item.helper && <div className="text-muted small">{item.helper}</div>}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="row g-3 align-items-stretch">
                <div className="col-12 col-md-4 col-xl-3">
                    <div className="border rounded-4 p-3 h-100 shadow-sm bg-white d-flex align-items-start gap-3">
                        <div className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center" style={{ width: 44, height: 44 }}>
                            <IconUserCircle size={28} color="#2563eb" />
                        </div>
                        <div>
                            <div className="text-uppercase text-muted fw-semibold small">{t.contactCard}</div>
                            <div className="fw-semibold">{details.contacts_group_name || details.contacts_contact_name || t.unknown}</div>
                            {details.contacts_email && <div className="small text-muted">{details.contacts_email}</div>}
                        </div>
                    </div>
                </div>
                <div className="col-12 col-md-8 col-xl-9">
                    <div
                        className={`rounded-4 p-3 h-100 shadow-sm d-flex align-items-center gap-3 ${
                            daysLeftNumeric !== null && daysLeftNumeric < 11 ? "bg-warning-subtle" : "bg-light"
                        }`}
                        style={{ border: "1px solid rgba(0,0,0,0.05)" }}
                    >
                        <div className="rounded-circle bg-white d-inline-flex align-items-center justify-content-center" style={{ width: 48, height: 48 }}>
                            <IconAlertTriangle size={26} color={daysLeftNumeric !== null && daysLeftNumeric < 11 ? "#b45309" : "#16a34a"} />
                        </div>
                        <div>
                            <div className="text-uppercase fw-semibold small text-muted">{t.warnings}</div>
                            <div className="fw-semibold">
                                {daysLeftNumeric === null && t.warnOk}
                                {daysLeftNumeric !== null && daysLeftNumeric < 0 && t.warnExpiredShort}
                                {daysLeftNumeric !== null && daysLeftNumeric >= 0 && daysLeftNumeric < 11 && t.warnSoon(daysLeftNumeric)}
                                {daysLeftNumeric !== null && daysLeftNumeric >= 11 && t.warnOk}
                            </div>
                            {daysLeftNumeric !== null && (
                                <div className="small text-muted">
                                    {t.expires}: {formatDate(details.date_expiration)}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="border rounded-4 p-3 shadow-sm bg-white">
                <div className="d-flex align-items-center gap-2 mb-3">
                    <IconClock size={20} />
                    <div className="fw-semibold text-uppercase small text-muted">{t.history}</div>
                </div>
                {parsedLogs.length === 0 ? (
                    <div className="text-muted">{t.noHistory}</div>
                ) : (
                    <div className="table-responsive">
                        <table className="table align-middle mb-0">
                            <thead>
                                <tr>
                                    <th style={{ width: "20%" }}>{t.historyDate}</th>
                                    <th style={{ width: "20%" }}>{t.event}</th>
                                    <th style={{ width: "20%" }}>{t.domain}</th>
                                    <th style={{ width: "40%" }}>{t.after}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {parsedLogs.map((log) => (
                                    <tr key={log.id}>
                                        <td className="small text-muted">{formatDate(log.created_date)}</td>
                                        <td className="fw-semibold">{log.event}</td>
                                        <td className="small text-muted">{log.domain}</td>
                                        <td>{renderSnapshot(log.after)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Dashboard;
